import {
  Button,
  Flex,
  Loader,
  ScrollView,
  SearchField,
} from "@aws-amplify/ui-react";
import { API, graphqlOperation } from "aws-amplify";
import { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import LocalizedStrings from "react-localization";
import { CModal, CModalBody, CModalHeader, CModalTitle } from "@coreui/react";

import {
  toolParametersByToolID,
  getParameter,
  listParameters,
  getTool,
} from "graphql/queries";
import { updateToolParameter } from "graphql/mutations";
import usePermissions from "hooks/usePermission";
import ConfirmModal from "components/utils/ConfirmModal";
import ToolParamatersTable from "components/tools/parameters/ToolParametersTable";
import ToolParameterCreateForm from "./ToolParameterCreateForm";
import ToolParameterUpdateForm from "./ToolParameterUpdateForm";

const ToolParameters = ({ toolID, toolName, visible, onClose }) => {
  const [canCreate, canUpdate, canDelete] = usePermissions([
    "tool:create-param",
    "tool:update-param",
    "tool:delete-param",
  ]);
  const [visibleState, setVisibleState] = useState(visible);
  const [toolParameters, setToolParameters] = useState([]);
  const [toolParametersLoading, setToolParametersLoading] = useState(false);
  const [paginationTokens, setPaginationTokens] = useState({
    previous: [],
    nextToken: null,
  });
  const [modalDetails, setModalDetails] = useState({
    visible: false,
    toolParameterId: null,
    toolParameterName: null,
  });
  const [modalToolParameter, setModalToolParameter] = useState({
    visible: false,
    toolParameterId: null,
  });

  const columnsMapped = useMemo(
    () =>
      toolParameters.map((item) => {
        return {
          ...item,
          parameterName: item.parameterNameStr || <Loader />,
          actions: (
            <>
              {canUpdate && (
                <Button
                  className="px-1"
                  children={strings.actions.edit}
                  variation="link"
                  size="small"
                  onClick={() => openModal("update", item.id)}
                />
              )}
              {canDelete && (
                <Button
                  className="px-1"
                  children={strings.actions.delete}
                  variation="link"
                  size="small"
                  onClick={() => {
                    setModalDetails({
                      visible: true,
                      toolParameterId: item.id,
                      toolParameterName: item.name,
                    });
                  }}
                />
              )}
            </>
          ),
        };
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [toolParameters]
  );

  const onNext = () => {
    fetchToolParameters(paginationTokens.nextToken);
  };

  const onPrevious = () => {
    const previous =
      paginationTokens.previous?.length <= 1
        ? null
        : paginationTokens.previous?.slice(-2)[0];
    fetchToolParameters(previous, false);
  };

  const onKeywordSubmit = (keyword) => {
    const keywordSplit = keyword && keyword.split(" ");
    fetchToolParameters(undefined, undefined, keywordSplit);
  };

  const openModal = (action, id) => {
    setVisibleState(false);
    setModalToolParameter({
      visible: true,
      action,
      toolParameterId: id,
    });
  };

  const closeModal = () => {
    setVisibleState(true);
    setModalToolParameter({
      visible: false,
    });
  };

  const onSuccessAction = () => {
    closeModal();
    fetchToolParameters();
  };

  async function fetchParameterName(id) {
    try {
      const parameterData = await API.graphql(
        graphqlOperation(getParameter, {
          id,
        })
      );
      const parameterItem = parameterData.data.getParameter;
      return parameterItem?.name ?? "";
    } catch (err) {
      console.log("Error fetchParameterName", err);
      return "";
    }
  }

  const getParameterNames = (list) => {
    list?.forEach(async (item) => {
      const parameterNameStr = await fetchParameterName(item.parameterID);

      setToolParameters((prevList) => {
        const updatedList = [...prevList];
        const index = updatedList.findIndex(
          (itemInd) => itemInd.id === item.id
        );

        updatedList[index] = {
          ...updatedList[index],
          parameterNameStr,
        };

        return updatedList;
      });
    });
  };

  async function getCompany(id) {
    const dataReturned = await API.graphql(graphqlOperation(getTool, { id }));
    const itemReturned = dataReturned.data.getTool;
    if (!itemReturned) {
      toast.error(strings.responses.errorFetch);
      return;
    }
    return itemReturned.companyID;
  }

  async function fetchToolParameters(token, isNext = true, keywords) {
    try {
      setToolParametersLoading(true);
      const limitMapped = 10;
      let filterObjList = [];

      if (keywords) {
        const companyID = await getCompany(toolID);
        const parametersData = await API.graphql(
          graphqlOperation(listParameters, {
            filter: {
              and: keywords.map((key) => ({
                or: [
                  {
                    name: { contains: key },
                  },
                ],
              })),
              companies: { contains: companyID },
              deletedAt: { attributeExists: false },
            },
          })
        );
        const parametersItems = parametersData.data.listParameters.items;

        filterObjList =
          parametersItems?.map((param) => ({
            parameterID: { eq: param.id },
          })) ?? [];
      }

      const toolParametersData = await API.graphql(
        graphqlOperation(toolParametersByToolID, {
          toolID,
          limit: !keywords ? limitMapped : undefined,
          nextToken: token,
          filter: keywords
            ? {
                and: keywords.map((key) => ({
                  or: [
                    ...filterObjList,
                    {
                      parameterValue: { contains: key },
                    },
                  ],
                })),
                deletedAt: { attributeExists: false },
              }
            : { deletedAt: { attributeExists: false } },
        })
      );
      const toolParametersItems =
        toolParametersData.data.toolParametersByToolID.items;
      setToolParameters(toolParametersItems);
      getParameterNames(toolParametersItems);
      setPaginationTokens((prevSt) => {
        let previous = prevSt.previous ?? [];
        if (isNext) previous = token ? [...previous, token] : [...previous];
        if (!isNext && previous?.length) previous.pop();

        return {
          previous,
          nextToken: toolParametersData.data.toolParametersByToolID.nextToken,
        };
      });
      setToolParametersLoading(false);
    } catch (err) {
      toast.error(strings.responses.errorFetch);
      setToolParametersLoading(false);
    } finally {
      setToolParametersLoading(false);
    }
  }

  async function toolParameterDelete(id) {
    try {
      const result = await API.graphql(
        graphqlOperation(updateToolParameter, {
          input: { id, deletedAt: new Date().toISOString() },
        })
      );
      toast.success(strings.successDeleted);
      const deletedId = result.data.updateToolParameter.id;
      setToolParameters(toolParameters.filter((item) => item.id !== deletedId));
      setModalDetails({
        visible: false,
        toolParameterId: null,
      });
    } catch (err) {
      toast.error(strings.responses[err]);
    }
  }

  useEffect(() => {
    fetchToolParameters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setVisibleState(visible);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  return (
    <>
      <CModal
        size="xl"
        alignment="center"
        visible={visibleState}
        onClose={() => !modalToolParameter.visible && onClose()}
        backdrop="static"
      >
        <CModalHeader>
          <CModalTitle>
            {strings.title} - {toolName}
          </CModalTitle>
        </CModalHeader>
        <CModalBody style={{ overflow: "hidden" }}>
          <ScrollView maxHeight="75vh">
            <div>
              <div className="p-3">
                <Flex className="mb-1" justifyContent="space-between">
                  <div style={styles.searchByKeyword}>
                    <SearchField
                      label={strings.table.search.label}
                      placeholder={strings.table.search.placeholder}
                      onSubmit={onKeywordSubmit}
                      onClear={onKeywordSubmit}
                    />
                  </div>
                  {canCreate && (
                    <Button
                      style={styles.buttonAdd}
                      children={strings.actions.addToolParameter}
                      variation="primary"
                      onClick={() => openModal("create")}
                    />
                  )}
                </Flex>
                {toolParametersLoading ? (
                  <Loader />
                ) : (
                  <ToolParamatersTable
                    items={columnsMapped}
                    pagination={paginationTokens}
                    onNext={onNext}
                    onPrevious={onPrevious}
                  />
                )}
              </div>
              <ConfirmModal
                visible={modalDetails.visible}
                body={strings.formatString(
                  strings.modal.confirmDeleteQuestion,
                  {
                    name: modalDetails.toolParameterName,
                  }
                )}
                onCancel={() =>
                  setModalDetails({
                    visible: false,
                    toolParameterId: null,
                  })
                }
                onConfirm={() =>
                  toolParameterDelete(modalDetails.toolParameterId)
                }
              />
            </div>
          </ScrollView>
        </CModalBody>
      </CModal>
      {modalToolParameter.action && (
        <CModal
          size="xl"
          alignment="center"
          visible={modalToolParameter.visible}
          onClose={closeModal}
          backdrop="static"
        >
          <CModalHeader>
            <CModalTitle>
              {modalToolParameter.action === "create" ? (
                <>
                  {strings.modalToolParameter.addTitle} - {toolName}
                </>
              ) : (
                <>
                  {strings.modalToolParameter.updateTitle} - {toolName}
                </>
              )}
            </CModalTitle>
          </CModalHeader>
          <CModalBody style={{ overflow: "hidden" }}>
            <ScrollView maxHeight="75vh">
              {modalToolParameter.action === "create" ? (
                <ToolParameterCreateForm
                  toolID={toolID}
                  onSuccess={onSuccessAction}
                />
              ) : (
                <ToolParameterUpdateForm
                  toolID={toolID}
                  toolParameterId={modalToolParameter.toolParameterId}
                  onSuccess={onSuccessAction}
                />
              )}
            </ScrollView>
          </CModalBody>
        </CModal>
      )}
    </>
  );
};

const strings = new LocalizedStrings({
  es: {
    title: "Parámetros de Herramienta",
    successDeleted: "Parámetro de Herramienta eliminado con éxito",
    actions: {
      addToolParameter: "Agregar Parámetro de Herramienta",
      hide: "Ocultar",
      edit: "Editar",
      delete: "Eliminar",
    },
    responses: {
      errorFetch: "Error al consultar parámetros de herramienta",
    },
    table: {
      search: {
        label: "Buscar",
        placeholder: "Buscar por palabra clave...",
      },
    },
    modal: {
      confirmDeleteQuestion:
        "¿Estás seguro de eliminar el parámetro de herramienta {name}?",
    },
    modalToolParameter: {
      addTitle: "Agregar Parámetro de Herramienta",
      updateTitle: "Actualizar Parámetro de Herramienta",
    },
  },
});

const styles = {
  buttonAdd: { height: "fit-content" },
  searchByKeyword: { marginBottom: "15px", width: "25%", minWidth: "300px" },
};

export default ToolParameters;
